/**
 * Pure helpers for {@link SidebarRail}.
 *
 * Extracted verbatim from SidebarRail.tsx (zero behavior change).
 */

/** Calculate position-aware animation distance for module visibility toggle.
 * @returns Distance value to use in animation, or null if animation should be skipped.
 */
export function calculateToggleAnimDistance(
  toggledKey: string,
  direction: 'up' | 'down',
  visibility: Record<string, boolean>,
  allKeysInOrder: string[],
): number | null {
  const toggledIndex = allKeysInOrder.indexOf(toggledKey);

  if (direction === 'up') {
    // Module being enabled: slides up from off-section to its position.
    // Distance = how many visible modules come after it (+ 1 for the item itself).
    const visibleAfterToggled = allKeysInOrder
      .slice(toggledIndex + 1)
      .filter((k) => visibility[k] !== false).length;

    if (visibleAfterToggled === 0) {
      // #1868: Module lands at the bottom — no animation, appear in place.
      return null;
    }

    return Math.max(2, visibleAfterToggled + 1);
  } else {
    // Module being disabled: slides down to off-section.
    // Distance = how many visible modules come before it (+ 1 for movement to off-section).
    const visibleBeforeToggled = allKeysInOrder
      .slice(0, toggledIndex)
      .filter((k) => visibility[k] !== false).length;
    return Math.max(2, visibleBeforeToggled + 1);
  }
}

/**
 * Whether a workspace module should surface as a primary rail item.
 *
 * Detected via the explicit `isPrimaryRail` flag on the module (clean model),
 * or `pinned: true` in the user's ModuleSidebarPreference (seeder transition
 * signal via /module-preferences). The legacy archetype-label fallback was
 * removed in #3235 once seeded data carried the flag/pin signals.
 *
 * NOTE: the API serializes this field in camelCase (`isPrimaryRail`) via
 * `mapModuleEntity`; reading `is_primary_rail` (snake_case) here would always
 * be `undefined` at runtime and silently disable the flag path.
 *
 * Archived modules are never primary-rail items.
 */
export function isPrimaryRailModule(
  module: { id: string; archivedAt?: string; isPrimaryRail?: boolean },
  pinnedModuleIds: ReadonlySet<string>,
): boolean {
  if (module.archivedAt) return false;
  const hasPrimaryFlag = module.isPrimaryRail === true;
  const isPinned = pinnedModuleIds.has(module.id);
  return hasPrimaryFlag || isPinned;
}

/**
 * Sort visible rail items by a saved key order (Teams "move" mode). Keys absent
 * from `order` keep their original relative position (sorted after the ordered
 * ones). An empty `order` is a no-op (returns the input order). Pure — does not
 * mutate `items`.
 */
export function sortRailItemsByOrder<T extends { key: string }>(
  items: T[],
  order: readonly string[],
): T[] {
  if (!order.length) return items;
  return [...items].sort((a, b) => {
    const ra = order.indexOf(a.key);
    const rb = order.indexOf(b.key);
    return (
      (ra === -1 ? Number.MAX_SAFE_INTEGER : ra) -
      (rb === -1 ? Number.MAX_SAFE_INTEGER : rb)
    );
  });
}

/**
 * Merge a reordered list of CURRENTLY-VISIBLE category keys back together with
 * the previously-saved order, re-inserting any saved keys missing from the new
 * list (e.g. a category hidden in manager mode) at their previous index. This is
 * what stops a reorder from wiping a hidden category's saved slot (#3741). Pure.
 */
export function mergeRailOrder(
  orderedKeys: string[],
  prevOrder: readonly string[],
): string[] {
  const mergedOrder = [...orderedKeys];
  prevOrder.forEach((key, idx) => {
    if (!mergedOrder.includes(key)) {
      mergedOrder.splice(Math.min(idx, mergedOrder.length), 0, key);
    }
  });
  return mergedOrder;
}

/**
 * Whether a click on the currently-highlighted rail item should toggle-CLOSE
 * the module pane (ADR 028). Close fires ONLY when the pane is CONFIRMED on
 * screen: visible, targeting a module, and ChatBox has reported that exact
 * module rendered. Any unconfirmed highlight — pane still opening/attaching,
 * ChatBox unmounted, or state drift — resolves as open-intent instead, so a
 * click can never be swallowed as a close against a pane that isn't showing
 * (the intermittent "sidebar click does nothing" bug). Pure.
 */
export function shouldToggleCloseOnRailClick(params: {
  railKey: string;
  activeRailKey: string | null;
  isPaneVisible: boolean;
  paneModuleId: string | null;
  renderedModuleId: string | null;
}): boolean {
  const { railKey, activeRailKey, isPaneVisible, paneModuleId, renderedModuleId } = params;
  if (activeRailKey !== railKey) return false;
  return (
    isPaneVisible
    && paneModuleId !== null
    && renderedModuleId === paneModuleId
  );
}
